import React, { useCallback, useEffect, useState, useRef } from "react"
import useWebSocket, { ReadyState } from "react-use-websocket"
import { IWebsocketState, WebsocketContext } from "./WebsocketContext"

interface IProps {
  cognitoToken: string
  userId: string
  feedbackCanvasId: string
  children: React.ReactNode
}

const WEBSOCKET_URL = process.env.REACT_APP_AWS_WEBSOCKET_API_URL || ""

// Função para decodificar JWT e obter tempo de expiração
const getTokenExpirationTime = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp * 1000 // Converter para milliseconds
  } catch (error) {
    console.error('Erro ao decodificar token:', error)
    return null
  }
}

// Função para verificar se o token vai expirar em breve (próximos 5 minutos)
const shouldRefreshToken = (token: string): boolean => {
  const expirationTime = getTokenExpirationTime(token)
  if (!expirationTime) return false

  const now = Date.now()
  const timeUntilExpiration = expirationTime - now
  const fiveMinutes = 5 * 60 * 1000 // 5 minutos em milliseconds

  return timeUntilExpiration <= fiveMinutes
}

export const WebsocketContextProvider: React.FunctionComponent<IProps> = ({ cognitoToken, userId, feedbackCanvasId, children }: IProps): React.JSX.Element => {
  const [currentToken, setCurrentToken] = useState(cognitoToken)
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const subprotocol = ["aws-appsync-event-ws", currentToken]

  // Função para executar refresh do token
  const refreshToken = useCallback(() => {
    const refreshTokenValue = localStorage.getItem('refreshToken')

    if (!refreshTokenValue) {
      console.error('[WebSocket] Refresh token não encontrado no localStorage')
      return
    }

    console.log('[WebSocket] Iniciando refresh do token...')

    sendJsonMessage({
      action: "refresh_token",
      data: {
        refresh_token: refreshTokenValue
      }
    })
  }, [])

  // Função para agendar o próximo refresh
  const scheduleTokenRefresh = useCallback((token: string) => {
    const expirationTime = getTokenExpirationTime(token)
    if (!expirationTime) return

    const now = Date.now()
    const timeUntilExpiration = expirationTime - now
    const refreshTime = Math.max(timeUntilExpiration - (5 * 60 * 1000), 30000) // Refresh 5min antes ou em 30s no mínimo

    // Limpar timeout anterior se existir
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
    }

    // Agendar novo refresh
    refreshTimeoutRef.current = setTimeout(() => {
      if (shouldRefreshToken(token)) {
        refreshToken()
      }
    }, refreshTime)

    console.log(`[WebSocket] Próximo refresh agendado em ${Math.round(refreshTime / 1000)}s`)
  }, [refreshToken])

  const { readyState, sendJsonMessage, lastJsonMessage, getWebSocket } = useWebSocket(WEBSOCKET_URL, {
    share: false,
    shouldReconnect: () => true,
    protocols: subprotocol,
    reconnectAttempts: 5,
    reconnectInterval: 2000,
    retryOnError: false,
    queryParams: {
      id: feedbackCanvasId,
      user_id: userId,
    },
  })

  const connectionStatus = {
    [ReadyState.UNINSTANTIATED]: "Uninstantiated",
    [ReadyState.CONNECTING]: "Connecting",
    [ReadyState.OPEN]: "Open",
    [ReadyState.CLOSING]: "Closing",
    [ReadyState.CLOSED]: "Closed",
  }[readyState]

  useEffect(() => {
    console.log("[WebSocket] State:", connectionStatus)

    if (readyState === ReadyState.OPEN) {
      console.log("[WebSocket] Enviando mensagem subscribe para canvas:", feedbackCanvasId)
      sendJsonMessage({
        id: feedbackCanvasId,
        action: "subscribe",
        data: {
          channel: `feedback-canvas-channel-${feedbackCanvasId}`,
        },
      })
    }
  }, [readyState, sendJsonMessage, feedbackCanvasId, connectionStatus])

  // Effect para processar mensagens recebidas e gerenciar refresh de token
  useEffect(() => {
    console.log("[WebSocket] Receive:", lastJsonMessage)

    if (lastJsonMessage && typeof lastJsonMessage === 'object') {
      const message = lastJsonMessage as any

      // Processar sucesso do refresh token
      if (message.action === 'refresh_token_success' && message.data) {
        const newTokens = message.data

        console.log('[WebSocket] Tokens atualizados com sucesso')

        // Atualizar tokens no localStorage
        if (newTokens.id_token) {
          localStorage.setItem('idToken', newTokens.id_token)
          setCurrentToken(newTokens.id_token)
        }

        if (newTokens.access_token) {
          localStorage.setItem('accessToken', newTokens.access_token)
        }

        // Agendar próximo refresh
        if (newTokens.id_token) {
          scheduleTokenRefresh(newTokens.id_token)
        }
      }

      // Processar erro do refresh token
      else if (message.action === 'refresh_token_error') {
        console.error('[WebSocket] Erro ao renovar token:', message.data)

        // Aqui você pode implementar uma lógica para redirecionar para login
        // ou tentar novamente após um tempo
      }
    }
  }, [lastJsonMessage, scheduleTokenRefresh])

  const send = useCallback(
    (action: string, jsonData: any): void => {
      console.log("[WebSocket] Send:", feedbackCanvasId, action, jsonData)
      sendJsonMessage({
        id: feedbackCanvasId,
        action: action,
        data: jsonData,
      })
    },
    [sendJsonMessage, feedbackCanvasId]
  )

  const disconnect = () => {
    // Limpar timeout de refresh ao desconectar
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
    }
    getWebSocket()?.close()
  }

  // Ao fechar a conexão, limpar agendamentos e tentar renovar token para próxima reconexão
  useEffect(() => {
    if (readyState === ReadyState.CLOSING || readyState === ReadyState.CLOSED) {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
    }

    if (readyState === ReadyState.CLOSED) {
      // Tentar renovar o token imediatamente; o hook fará a reconexão automática
      refreshToken()
    }
  }, [readyState, refreshToken])

  // Effect inicial para agendar o primeiro refresh do token
  useEffect(() => {
    if (currentToken && readyState === ReadyState.OPEN) {
      scheduleTokenRefresh(currentToken)
    }

    // Cleanup function
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
    }
  }, [currentToken, readyState, scheduleTokenRefresh])

  const contextValue: IWebsocketState = {
    connectionStatus,
    lastJsonMessage,
    readyState,
    disconnect,
    send,
  }

  return <WebsocketContext.Provider value={contextValue}>{children}</WebsocketContext.Provider>
}
